import React, { useEffect, useState, useCallback } from "react";
import { Table, Tag, Typography, Select, Space, Button, Card } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { privateAdminGetApi } from "../../utils/apiHelper";

const { Title, Text } = Typography;

function ActiveUsersChart({ organizationId }) {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchSeries = useCallback(() => {
    setLoading(true);
    const query = { hours: 6 };
    if (organizationId) query.organizationId = organizationId;
    privateAdminGetApi("admin/user-logs/active-series", query)
      .then((res) => {
        setSeries(
          res.data.result.series.map((row) => ({
            time: new Date(row.time).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
            activeUsers: row.activeUsers,
          })),
        );
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [organizationId]);

  useEffect(() => {
    fetchSeries();
  }, [fetchSeries]);

  return (
    <Card
      size="small"
      title="Active users (last 6 hours, 5-minute buckets)"
      style={{ marginBottom: 16 }}
      loading={loading}
      extra={
        <Button size="small" icon={<ReloadOutlined />} onClick={fetchSeries} />
      }
    >
      {series.length ? (
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={series}>
            <CartesianGrid stroke="#E8ECEF" strokeDasharray="3 3" />
            <XAxis dataKey="time" tick={{ fontSize: 11 }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
            <Tooltip />
            <Line
              type="monotone"
              dataKey="activeUsers"
              name="Active users"
              stroke="#4A5A61"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <Text type="secondary">No activity in the last 6 hours.</Text>
      )}
    </Card>
  );
}

const METHOD_OPTIONS = [
  { value: "LOGIN", label: "LOGIN" },
  { value: "POST", label: "POST (Created)" },
  { value: "PUT", label: "PUT (Updated)" },
  { value: "DELETE", label: "DELETE (Deleted)" },
];

const METHOD_COLORS = {
  LOGIN: "purple",
  POST: "green",
  PUT: "blue",
  DELETE: "red",
};

export default function AdminUserLogs() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgs, setOrgs] = useState([]);
  const [organizationId, setOrganizationId] = useState(undefined);
  const [method, setMethod] = useState(undefined);

  useEffect(() => {
    privateAdminGetApi("admin/organizations").then((res) => {
      setOrgs(res.data.result.records);
    });
  }, []);

  const fetchLogs = useCallback(() => {
    setLoading(true);
    const query = {};
    if (organizationId) query.organizationId = organizationId;
    if (method) query.method = method;
    privateAdminGetApi("admin/user-logs", query)
      .then((res) => {
        setRecords(res.data.result.records);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [organizationId, method]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const columns = [
    {
      title: "Time",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (v) => new Date(v).toLocaleString(),
      width: 190,
    },
    {
      title: "Organization",
      dataIndex: "organizationName",
      key: "organizationName",
      render: (v) => v || "-",
    },
    { title: "User", dataIndex: "emailAddress", key: "emailAddress" },
    {
      title: "Action",
      dataIndex: "method",
      key: "method",
      render: (v) => <Tag color={METHOD_COLORS[v] || "default"}>{v}</Tag>,
    },
    { title: "Resource", dataIndex: "resource", key: "resource" },
    { title: "Resource ID", dataIndex: "resourceId", key: "resourceId" },
    { title: "IP", dataIndex: "ip", key: "ip", render: (v) => v || "-" },
    {
      title: "User agent",
      dataIndex: "userAgent",
      key: "userAgent",
      ellipsis: true,
      render: (v) => v || "-",
    },
  ];

  return (
    <div>
      <Space
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <Title level={5} style={{ marginBottom: 0 }}>
          User activity logs
        </Title>
        <Space>
          <Select
            allowClear
            placeholder="Organization"
            style={{ width: 220 }}
            value={organizationId}
            onChange={setOrganizationId}
            options={orgs.map((o) => ({ value: o._id, label: o.name }))}
          />
          <Select
            allowClear
            placeholder="Action"
            style={{ width: 180 }}
            value={method}
            onChange={setMethod}
            options={METHOD_OPTIONS}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchLogs} />
        </Space>
      </Space>
      <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        Every login and create/update/delete action taken by users across all
        organizations.
      </Text>
      <ActiveUsersChart organizationId={organizationId} />
      <Table
        rowKey="_id"
        loading={loading}
        columns={columns}
        dataSource={records}
        pagination={{ pageSize: 50 }}
        scroll={{ x: 900 }}
      />
    </div>
  );
}
